import React from "react"
import { useTranslation } from "react-i18next"
import AWS from "aws-sdk/global"
import { SNS } from "aws-sdk"

import { useForm, useAlert, useUser, useOperator } from "hooks"
import { formStatusEnum } from "components/Form/formUtils"

import { Spinner, Button } from "components/Elements"
import { FormElement } from "components/Form"
import { ADDRESS_ERROR_SUBJECT, OTHER_SUBJECT, subjectCollectionForSelect } from "utils/constants"
import { UserContact } from "models"
import { useNavigate } from "react-router-dom"
import { privateRoutes, publicRoutes } from "router/routes"
import { BICYCODE_ID } from "operators/bicycode/config"
import { DECATHLON_ID } from "operators/decathlon/config"
import { MFC_ID } from "operators/mfc/config"

const defaultFormFields: UserContact = {
	subject: "",
	fnuci: "",
	message: "",
	last_name: "",
	first_name: "",
	phone: "",
	mail: "",
}

interface ContactFormProps {
	isLoggedIn: boolean
}

const BIKE_ID_MAX_LENGTH = 14

export const ContactForm: React.FC<ContactFormProps> = ({ isLoggedIn }) => {
	const { currentOperator } = useOperator()
	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: defaultFormFields,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const [isMessageSent, setIsMessageSent] = React.useState<boolean>(false)

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { user } = useUser()
	const navigate = useNavigate()

	// Initialize the Amazon Cognito credentials provider
	AWS.config.region = process.env.REACT_APP_AWS_CONFIG_REGION // Region
	AWS.config.credentials = new AWS.CognitoIdentityCredentials({
		IdentityPoolId: process.env.REACT_APP_AWS_IDENTITY_POOL_ID,
	})

	const isLoading = formStatus === formStatusEnum.loading
	const isFnuciNotRequired =
		formFields.subject === OTHER_SUBJECT || formFields.subject === ADDRESS_ERROR_SUBJECT

	React.useEffect(() => {
		if (user && isLoggedIn) {
			setFormFields((formFields: UserContact) => ({
				...formFields,
				last_name: user.description.last_name,
				first_name: user.description.first_name,
				phone: user.description.phone,
				mail: user.description.mail,
			}))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoggedIn, user])

	const TopicArnDependingOnOperator = () => {
		switch (currentOperator?.id) {
			case BICYCODE_ID:
				return process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM_BC
			case DECATHLON_ID:
				return process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM_DK
			case MFC_ID:
				return process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM_MFC
		}
	}

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			// hide error
			setShowLocalErrors(false)
			// prevent form to be submitted again
			setFormStatus(formStatusEnum.loading)

			const { last_name, first_name, mail, phone, message, subject, fnuci } = formFields

			const sns = new SNS()
			const content = `
				Nom: ${last_name}\n
				Prénom: ${first_name}\n
				Email: ${mail}\n
				Téléphone: ${phone}\n
				${fnuci && `Code FNUCI: ${fnuci}\n`}
				Sujet: ${subject}\n
				Message: ${message}\n
			`
			const params = {
				Message: content,
				Subject: `${subject} ${fnuci && "-"} ${fnuci}`,
				TopicArn: TopicArnDependingOnOperator(),
			}
			sns.publish(params, function (err, data) {
				if (err) {
					//console.log(err, err.stack)
					alert("error", err.stack) // an error occurred
					setFormStatus(formStatusEnum.ready)
				} else {
					//console.log(data)
					setIsMessageSent(true) // successful response
				}
			})
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<form className="max-w-screen-sm mx-auto">
			{!isMessageSent && (
				<>
					<FormElement
						{...formElementCommonProps}
						value={formFields.subject}
						options={subjectCollectionForSelect}
						name="subject"
						id="subject"
						type="select"
						label={t("subject", { ns: "contact" })}
						className="my-2 px-1"
					/>

					{formFields.subject && (
						<FormElement
							{...formElementCommonProps}
							value={formFields.fnuci}
							name="fnuci"
							type="text"
							label={t("fnuci_code", { ns: "contact" })}
							className="my-1 px-1"
							required={!isFnuciNotRequired}
							linkedInputValues={isFnuciNotRequired}
							maxLength={BIKE_ID_MAX_LENGTH}
						/>
					)}

					{formFields.subject && !isLoggedIn && (
						<>
							<div className="flex justify-between flex-wrap">
								{/* Prénom */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.first_name}
									name="first_name"
									type="text"
									label={t("firstname", {
										ns: "user",
									})}
									className="w-full lg:w-2/4 my-1 px-1"
									required={true}
								/>

								{/* Nom */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.last_name}
									name="last_name"
									type="text"
									label={t("lastname", {
										ns: "user",
									})}
									className="w-full lg:w-2/4 my-1 px-1"
									required={true}
								/>
							</div>

							{/* Téléphone */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.phone}
								name="phone"
								type="phone"
								label={t("phone", { ns: "user" })}
								className="my-2 px-1"
								required={true}
							/>

							<FormElement
								{...formElementCommonProps}
								value={formFields.mail}
								name="mail"
								type="email"
								id="mail"
								label={t("email", { ns: "user" })}
								className="my-2 px-1"
								required={true}
							/>
						</>
					)}

					{formFields.subject && (
						<>
							<FormElement
								{...formElementCommonProps}
								value={formFields.message}
								name="message"
								type="textarea"
								id="textarea"
								label={t("message", { ns: "contact" })}
								className="my-2 px-1"
								required={true}
							/>

							{!isLoading && (
								<div className="flex mt-10 justify-center">
									<Button
										variant="secondary"
										type="submit"
										onClick={handleSubmit}
										className="mx-1 px-10 py-1 lg:mx-3"
									>
										{t("global.confirm")}
									</Button>
								</div>
							)}

							{isLoading && (
								<div className="flex justify-center my-8">
									<Spinner color={"var(--primaryColor)"} size={"100px"} />
								</div>
							)}
						</>
					)}
				</>
			)}

			{isMessageSent && (
				<div className="flex flex-col text-center bg-blue30 px-10 py-8 my-12 rounded-base">
					<p className="font-bold">{t("email_send", { ns: "form" })}</p>
					<Button
						variant="primary"
						type="submit"
						onClick={() =>
							isLoggedIn
								? navigate(privateRoutes.dashboard.path)
								: navigate(publicRoutes.signin.path)
						}
						className="px-10 py-1 mt-6"
						size="small"
					>
						{isLoggedIn
							? t("go_back_dashboard", { ns: "contact" })
							: t("go_back_login", { ns: "contact" })}
					</Button>
				</div>
			)}
		</form>
	)
}
